export type FollowUpSuggestion = {
  id: string;
  titleKey: string;
  prompt: string;
  tint: string;
};

// Static, deterministic follow-up suggestions keyed by which tool drove the
// turn. They are not LLM-generated. Grouped the same way as CARD_MAP in
// tool-cards/index.native.tsx (Jobs/CV/Profile/Applications/Candidates/...).
// Not every tool needs its own entry: extend by adding more tool names to an
// existing group, or a new group, as real usage calls for it.
const GROUP_SUGGESTIONS = {
  jobs: [
    { id: 'jobs.applyFirst', titleKey: 'chat.followUps.jobs.applyFirst', prompt: 'Prepare an application for the first job', tint: '#7986FE' },
    { id: 'jobs.refineSearch', titleKey: 'chat.followUps.jobs.refineSearch', prompt: 'Change my job search', tint: '#7986FE' },
  ],
  cv: [
    { id: 'cv.tailorForJob', titleKey: 'chat.followUps.cv.tailorForJob', prompt: 'Tailor this CV for a specific job', tint: '#A379FE' },
    // Discovery: most users never learn the CV skills exist. A CV turn is the
    // moment they are most useful, so offer them right after one.
    { id: 'cv.browseSkills', titleKey: 'chat.followUps.cv.browseSkills', prompt: 'Which CV skills could improve this CV?', tint: '#A379FE' },
    { id: 'cv.exportPdf', titleKey: 'chat.followUps.cv.exportPdf', prompt: 'Export this as a PDF', tint: '#A379FE' },
  ],
  profile: [
    { id: 'profile.reviewProfile', titleKey: 'chat.followUps.profile.reviewProfile', prompt: 'Show me my full profile', tint: '#79D2FE' },
    { id: 'profile.addExperience', titleKey: 'chat.followUps.profile.addExperience', prompt: 'Add another work experience', tint: '#79D2FE' },
  ],
  applications: [
    { id: 'applications.trackStatus', titleKey: 'chat.followUps.applications.trackStatus', prompt: 'Track the status of this application', tint: '#799FFE' },
    { id: 'applications.viewOthers', titleKey: 'chat.followUps.applications.viewOthers', prompt: 'Show my other applications', tint: '#799FFE' },
  ],
  candidates: [
    { id: 'candidates.viewTop', titleKey: 'chat.followUps.candidates.viewTop', prompt: 'View the top candidate', tint: '#799FFE' },
    { id: 'candidates.shortlist', titleKey: 'chat.followUps.candidates.shortlist', prompt: 'Shortlist this candidate', tint: '#799FFE' },
  ],
} as const satisfies Record<string, readonly FollowUpSuggestion[]>;

type SuggestionGroup = keyof typeof GROUP_SUGGESTIONS;

const TOOL_TO_GROUP: Record<string, SuggestionGroup> = {
  // Jobs
  jobSearch: 'jobs',
  jobRecommend: 'jobs',
  catalogJobSearch: 'jobs',
  // CV
  cvCreate: 'cv',
  cvUpdate: 'cv',
  cvUpdateSummary: 'cv',
  cvTailor: 'cv',
  // Profile
  profileUpdate: 'profile',
  experienceUpsert: 'profile',
  educationUpsert: 'profile',
  // Applications
  applicationSubmit: 'applications',
  applicationTrack: 'applications',
  catalogApplySubmit: 'applications',
  // Candidates (employer mode)
  candidateSearch: 'candidates',
  candidateShortlist: 'candidates',
};

export function getFollowUpSuggestions(
  toolName: string | undefined,
): readonly FollowUpSuggestion[] {
  if (!toolName) return [];
  const group = TOOL_TO_GROUP[toolName];
  return group ? GROUP_SUGGESTIONS[group] : [];
}
